import React, { useCallback, useEffect } from 'react';
import { useForm, FormProvider, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { debounce, Dialog, DialogTitle, DialogActions, DialogContent, Box, Typography, Button, TextField, IconButton, Grid } from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useCreateCustomField, useUpdateCustomField } from 'app/data/generic-queries';
import { ControlledSelect, ControlledDatePicker } from 'app/custom-components/form-components';
import { dataTypesMap, producerCategoriesMap, parseCustomFieldBounds } from '../utils';
import { CustomFieldsSchema } from '../CustomFields.schema';

const CreateCustomFieldDialog = ({ open, onClose, defaultValues, role }) => {
    const methods = useForm({
        defaultValues: {
            ...defaultValues,
            options: defaultValues?.options?.map((option) => ({ value: option })) || [{ value: '' }],
        },
        shouldUnregister: true,
        resolver: yupResolver(CustomFieldsSchema),
    });
    const { handleSubmit, register, reset, control } = methods;
    const { mutateAsync: createCustomField } = useCreateCustomField({ type: 'user', key: 'custom-fields' });
    const { mutateAsync: updateCustomField } = useUpdateCustomField({ type: 'user', key: 'custom-fields' });

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'options',
    });

    useEffect(() => {
        if (open && defaultValues) {
            reset({
                ...defaultValues,
                options: defaultValues?.options?.map((option) => ({ value: option })) || [{ value: '' }],
            });
        }
    }, [open, defaultValues, reset]);

    const dataTypeOptions = Object.entries(dataTypesMap).map(([key, value]) => ({ value: key, label: value }));
    const categoryOptions = Object.entries(producerCategoriesMap).map(([key, value]) => ({ value: key, label: value }));

    const onSubmit = async (customField) => {
        // min/max are dropped from the spread and re-added only when they parse, so a blank
        // bound is omitted rather than sent as '' — see parseCustomFieldBounds.
        const { min, max, ...rest } = customField;
        const data = {
            ...rest,
            role,
            ...parseCustomFieldBounds(customField),
        };
        if (customField.options) data.options = customField.options?.map((option) => option.value.trim()).filter(Boolean);
        if (defaultValues?._id) {
            await updateCustomField({ id: defaultValues._id, data });
        } else {
            await createCustomField({ data });
        }
        onClose();
    };
    const debounceSubmit = useCallback(debounce(onSubmit, 500), [defaultValues]);

    const { errors } = methods.formState;
    const isSelect = methods.watch('data_type') === 'select';
    const isNumber = methods.watch('data_type') === 'number';
    const isDate = methods.watch('data_type') === 'date';

    if (!defaultValues) return null;

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <FormProvider {...methods}>
                <DialogTitle>{!!defaultValues?.name ? 'Edit' : 'Create'} Custom Field</DialogTitle>
                <DialogContent>
                    <Box>
                        <Typography variant="subtitle1">Field Name</Typography>
                        <TextField sx={{ my: 1 }} fullWidth placeholder="Enter field name" {...register('name')} error={!!errors.name} helperText={errors.name?.message} />
                        <Typography variant="subtitle1">Input Type</Typography>
                        <ControlledSelect options={dataTypeOptions} name="data_type" label="input type" fullWidth />
                        {isSelect && (
                            <Box sx={{ my: 1, ml: 3 }}>
                                {fields.map((field, index) => (
                                    <Grid container spacing={1} key={field.id} sx={{ mb: 1 }}>
                                        <Grid item xs={10}>
                                            <TextField fullWidth size="small" placeholder="Enter option" {...register(`options.${index}.value`)} error={!!errors.options?.[index]?.value} helperText={errors.options?.[index]?.value?.message} />
                                        </Grid>
                                        <Grid item xs={2} sx={{ display: 'flex', alignItems: 'center' }}>
                                            <IconButton onClick={() => remove(index)} disabled={fields.length === 1} color="error">
                                                <DeleteIcon />
                                            </IconButton>
                                        </Grid>
                                    </Grid>
                                ))}
                                <Button variant="outlined" startIcon={<AddIcon />} onClick={() => append({ value: '' })} size="small">
                                    Add Option
                                </Button>
                            </Box>
                        )}
                        {isNumber && (
                            <Box sx={{ my: 1, ml: 3 }}>
                                <Box sx={{ mb: 1 }}>
                                    <TextField fullWidth size="small" placeholder="Enter min" {...register('min')} error={!!errors.min} helperText={errors.min?.message} type="number" />
                                </Box>
                                <Box sx={{ mb: 1 }}>
                                    <TextField fullWidth size="small" placeholder="Enter max" {...register('max')} error={!!errors.max} helperText={errors.max?.message} type="number" />
                                </Box>
                            </Box>
                        )}
                        {isDate && (
                            <Box sx={{ my: 1, ml: 3 }}>
                                <Box sx={{ mb: 1 }}>
                                    <ControlledDatePicker
                                        name="min"
                                        label="Min"
                                        slotProps={{
                                            textField: {
                                                size: 'small',
                                            },
                                        }}
                                    />
                                </Box>
                                <Box sx={{ mb: 1 }}>
                                    <ControlledDatePicker
                                        name="max"
                                        label="Max"
                                        slotProps={{
                                            textField: {
                                                size: 'small',
                                            },
                                        }}
                                    />
                                </Box>
                            </Box>
                        )}
                        <Typography variant="subtitle1">Field Location</Typography>
                        <ControlledSelect options={categoryOptions} name="category" label="field location" fullWidth />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button variant="contained" type="submit" onClick={handleSubmit(debounceSubmit, methods.errors)}>
                        Save
                    </Button>
                </DialogActions>
            </FormProvider>
        </Dialog>
    );
};

export default CreateCustomFieldDialog;
